import { DeleteOnboardingLocationButton } from '@/components/delete-onboarding-location-button';
import { ArchiveOrgDialog } from '@/components/archive-org-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/hooks/use-auth';
import { OrganizationWithProgress } from '@/hooks/use-organization-pipeline';
import { formatDate, getCurrentStage, getProgressBarColor } from '@/lib/dashboard-utils';
import { createClient } from '@/lib/supabase';
import { reactivateOrganization } from '@/lib/onboarding-status';
import { createClient as createMainAppClient } from '@supabase/supabase-js';
import { Archive, ArrowUpDown, Building2, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Loader2, MapPin, RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

interface OrganizationPipelineTableProps {
  organizations: OrganizationWithProgress[];
  loading: boolean;
  initialExpandedOrgId?: string | null;
  /** Called after an org is archived so parent can refresh its own data */
  onOrgArchived?: () => void;
  /** Called after a location is activated or deleted so parent can refresh */
  onLocationChanged?: () => void;
}

interface Location {
  id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  status: string | null;
}

interface ArchivedLocationInfo {
  id: string;
  name: string;
  logo_url: string | null;
  /** true = fully archived (archived_at set); false = pending archive (subscription still running) */
  isFullyArchived: boolean;
}

function ExpandedOrgDetails({ orgId, onLocationChanged }: { orgId: string; onLocationChanged?: () => void }) {
  const [org, setOrg] = useState<any>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [archivedLocations, setArchivedLocations] = useState<ArchivedLocationInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<Record<string, 'activating' | 'deleting'>>({});
  const { user } = useAuth();

  const fetchDetails = useCallback(async () => {
    try {
      setLoading(true);
      const supabase = createClient();

      // Fetch organization details
      const { data: orgData } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single();

      setOrg(orgData);

      // Fetch locations from ops portal DB (exclude deleted/deactivated)
      const { data: locData } = await supabase
        .from('locations')
        .select('id, name, description, logo_url, status')
        .eq('organization_id', orgId)
        .neq('status', 'DELETING')
        .neq('status', 'DEACTIVATED')
        .order('created_at');

      // Cross-reference with main app to find archived locations
      const mainAppUrl = import.meta.env.VITE_SUPABASE_MAIN_APP_URL;
      const serviceRoleKey = import.meta.env.VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY;
      let archivedIds = new Set<string>();
      let archivedLocs: ArchivedLocationInfo[] = [];

      if (mainAppUrl && serviceRoleKey) {
        const mainApp = createMainAppClient(mainAppUrl, serviceRoleKey, {
          auth: { autoRefreshToken: false, persistSession: false },
        });

        // Fetch fully archived locations (archived_at set)
        const { data: archivedData } = await mainApp
          .from('locations')
          .select('id, name, logo_url')
          .eq('organization_id', orgId)
          .is('deleted_at', null)
          .not('archived_at', 'is', null);

        if (archivedData) {
          archivedIds = new Set(archivedData.map((l: any) => l.id));
          archivedLocs = archivedData.map((l: any) => ({
            id: l.id,
            name: l.name,
            logo_url: l.logo_url,
            isFullyArchived: true,
          }));
        }

        // Fetch pending-archive locations (subscription still running, not yet archived)
        const { data: pendingArchiveData } = await mainApp
          .from('deactivate_locations')
          .select('location_id, location_name, location_logo')
          .eq('organization_id', orgId)
          .eq('source', 'org_archive')
          .eq('status', 'ACTIVE_UNTIL_PERIOD_END');

        if (pendingArchiveData) {
          for (const rec of pendingArchiveData) {
            if (!archivedIds.has(rec.location_id)) {
              archivedIds.add(rec.location_id);
              archivedLocs.push({
                id: rec.location_id,
                name: rec.location_name,
                logo_url: rec.location_logo,
                isFullyArchived: false,
              });
            }
          }
        }

        // Fetch ALL location IDs that still exist in the main app for this org.
        // Any ops portal location NOT in this set has been hard-deleted (user-initiated
        // deactivation completes with a full row delete, not just deleted_at).
        const { data: mainAppLocationIds } = await mainApp
          .from('locations')
          .select('id')
          .eq('organization_id', orgId);

        if (mainAppLocationIds) {
          const existsInMainApp = new Set(mainAppLocationIds.map((l: any) => l.id));
          for (const loc of locData || []) {
            if (!existsInMainApp.has(loc.id)) {
              archivedIds.add(loc.id);
            }
          }
        }
      }

      // Filter out archived locations from the active list
      setLocations((locData || []).filter((loc: any) => !archivedIds.has(loc.id)));
      setArchivedLocations(archivedLocs);
    } catch (err) {
      console.error('Error fetching org details:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleActivateLocation = async (locationId: string) => {
    if (!user?.id) return;
    setActionLoading((prev) => ({ ...prev, [locationId]: 'activating' }));
    try {
      await reactivateOrganization(orgId, user.id, [locationId]);
      await fetchDetails();
      onLocationChanged?.();
    } catch (err) {
      console.error('Failed to activate location:', err);
    } finally {
      setActionLoading((prev) => {
        const next = { ...prev };
        delete next[locationId];
        return next;
      });
    }
  };


  if (loading) {
    return (
      <div className='p-4 bg-gray-50 dark:bg-gray-900/50'>
        <p className='text-sm text-gray-600 dark:text-gray-400'>Loading...</p>
      </div>
    );
  }

  if (!org) return null;

  return (
    <div className='p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700'>
      <div className='space-y-4'>
        {/* Organization Details */}
        <div className='grid grid-cols-2 gap-4 text-sm'>
          <div>
            <span className='text-gray-600 dark:text-gray-400'>Organization ID:</span>
            <span className='ml-2 font-mono text-gray-900 dark:text-gray-100'>{org.id}</span>
          </div>
          <div>
            <span className='text-gray-600 dark:text-gray-400'>Created:</span>
            <span className='ml-2 text-gray-900 dark:text-gray-100'>{formatDate(org.created_at)}</span>
          </div>
        </div>

        {/* Active Locations */}
        {locations.length > 0 && (
          <div className='pt-4 border-t border-gray-200 dark:border-gray-700'>
            <h4 className='text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3'>Locations ({locations.length})</h4>
            <div className='space-y-2'>
              {locations.map((loc) => (
                <div
                  key={loc.id}
                  className='flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700'
                >
                  <div className='flex items-center gap-3 flex-1'>
                    {loc.logo_url ? (
                      <div className='w-10 h-10 rounded overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-700'>
                        <img
                          src={loc.logo_url}
                          alt={`${loc.name} logo`}
                          className='w-full h-full object-cover'
                        />
                      </div>
                    ) : (
                      <div className='w-10 h-10 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0'>
                        <Building2 className='w-5 h-5 text-gray-400' />
                      </div>
                    )}
                    <div className='flex-1 min-w-0'>
                      <p className='font-medium text-sm text-gray-900 dark:text-gray-100'>{loc.name}</p>
                      {loc.description && (
                        <p className='text-xs text-gray-600 dark:text-gray-400 truncate'>{loc.description}</p>
                      )}
                        {loc.status && (
                        <Badge className={`mt-1 text-xs ${
                          loc.status === 'ACTIVE' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                          loc.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {loc.status === 'ACTIVE' ? '✓ Active' : loc.status}
                        </Badge>
                        )}
                    </div>
                  </div>
                  {loc.status !== 'ACTIVE' && user?.role === 'SYSTEM_ADMIN' && (
                    <DeleteOnboardingLocationButton
                      organizationId={orgId}
                      location={loc as any}
                      userRole={user.role}
                      onDeleteSuccess={() => {
                        setLocations(locations.filter(l => l.id !== loc.id));
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Archived Locations */}
        {archivedLocations.length > 0 && (
          <div className='pt-4 border-t border-gray-200 dark:border-gray-700'>
            <h4 className='text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3'>
              Archived / Pending Archive ({archivedLocations.length})
            </h4>
            <div className='space-y-2'>
              {archivedLocations.map((loc) => {
                const locAction = actionLoading[loc.id];
                return (
                  <div
                    key={loc.id}
                    className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700'
                  >
                    <div className='flex items-center gap-3 flex-1'>
                      {loc.logo_url ? (
                        <div className='w-10 h-10 rounded overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-700 opacity-60'>
                          <img src={loc.logo_url} alt={loc.name} className='w-full h-full object-cover' />
                        </div>
                      ) : (
                        <div className='w-10 h-10 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0 opacity-60'>
                          <MapPin className='w-5 h-5 text-gray-400' />
                        </div>
                      )}
                      <div className='flex-1 min-w-0'>
                        <p className='font-medium text-sm text-gray-600 dark:text-gray-400'>{loc.name}</p>
                        {loc.isFullyArchived ? (
                          <Badge className='mt-1 text-[10px] bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 px-1.5 py-0'>
                            ARCHIVED
                          </Badge>
                        ) : (
                          <Badge className='mt-1 text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0'>
                            PENDING ARCHIVE
                          </Badge>
                        )}
                      </div>
                    </div>
                    {user?.role === 'SYSTEM_ADMIN' && (
                      <div className='flex items-center gap-2 shrink-0'>
                        <Button
                          size='sm'
                          variant='outline'
                          className='text-green-700 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-950/20'
                          onClick={() => handleActivateLocation(loc.id)}
                          disabled={!!locAction}
                        >
                          {locAction === 'activating' ? (
                            <Loader2 className='h-3.5 w-3.5 animate-spin' />
                          ) : (
                            <RefreshCw className='h-3.5 w-3.5 mr-1' />
                          )}
                          {locAction === 'activating' ? '' : 'Activate'}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const ITEMS_PER_PAGE = 15;

type SortField = 'name' | 'status' | 'progress' | 'locations' | 'updated';
type SortOrder = 'asc' | 'desc';

export function OrganizationPipelineTable({
  organizations,
  loading,
  initialExpandedOrgId,
  onOrgArchived,
  onLocationChanged,
}: OrganizationPipelineTableProps) {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('updated');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(initialExpandedOrgId || null);
  const [currentPage, setCurrentPage] = useState(1);
  // Archive dialog state
  const [archiveTarget, setArchiveTarget] = useState<{ id: string; name: string; locationCount: number } | null>(null);
  // Local set of org IDs that have been archived this session (for immediate UI feedback)
  const [locallyArchivedIds, setLocallyArchivedIds] = useState<Set<string>>(new Set());
  const { user } = useAuth();

  // Update expandedOrgId when initialExpandedOrgId changes
  useEffect(() => {
    if (initialExpandedOrgId) {
      setExpandedOrgId(initialExpandedOrgId);
    }
  }, [initialExpandedOrgId]);

  // Clear locallyArchivedIds for orgs that are no longer archive_pending/archived
  // (e.g. after reactivation, the fresh data shows the org as ACTIVATED again)
  useEffect(() => {
    if (locallyArchivedIds.size === 0) return;
    const stillArchived = new Set<string>();
    for (const id of locallyArchivedIds) {
      const org = organizations.find((o) => o.id === id);
      if (org && org.status === 'ACTIVATED') continue; // reactivated — drop from local set
      if (org) stillArchived.add(id); // still in a non-active state — keep
      // org not in list anymore (moved to archived section) — drop
    }
    if (stillArchived.size !== locallyArchivedIds.size) {
      setLocallyArchivedIds(stillArchived);
    }
  }, [organizations]);

  // Get unique statuses
  const statuses = useMemo(() => {
    const unique = new Set(organizations.map((o) => o.status));
    return Array.from(unique).sort();
  }, [organizations]);

  // Filter and sort organizations
  const allFilteredOrganizations = useMemo(() => {
    let filtered = organizations.filter((org) => {
      const matchesStatus = !statusFilter || org.status === statusFilter;
      const matchesSearch = !searchQuery || org.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'progress':
          aValue = a.progressPercentage;
          bValue = b.progressPercentage;
          break;
        case 'locations':
          aValue = a.locationCount;
          bValue = b.locationCount;
          break;
        case 'updated':
          aValue = new Date(a.lastActivityAt || '').getTime();
          bValue = new Date(b.lastActivityAt || '').getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [organizations, statusFilter, searchQuery, sortField, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(allFilteredOrganizations.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const filteredOrganizations = allFilteredOrganizations.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to page 1 when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery, sortField, sortOrder]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className='h-4 w-4 opacity-30' />;
    return sortOrder === 'asc' ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Organizations Pipeline Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='flex items-center justify-center py-8'>
            <p className='text-gray-600 dark:text-gray-400'>Loading organizations...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card className='overflow-hidden'>
      <CardHeader>
        <div className='flex flex-col gap-4'>
          <CardTitle>Organizations Pipeline Status</CardTitle>

          {/* Filters */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <Input
              placeholder='Search by organization name...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='h-10'
            />

            <Select value={statusFilter || 'ALL'} onValueChange={(value) => setStatusFilter(value === 'ALL' ? '' : value)}>
              <SelectTrigger className='h-10'>
                <SelectValue placeholder='All statuses' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='ALL'>All statuses</SelectItem>
                {statuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status.replace(/_/g, ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {filteredOrganizations.length === 0 ? (
          <div className='text-center py-8'>
            <Building2 className='h-12 w-12 mx-auto mb-4 text-gray-400' />
            <p className='text-gray-600 dark:text-gray-400'>No organizations matching filters</p>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm min-w-[800px]'>
              <thead className='border-b border-gray-200 dark:border-gray-700'>
                <tr className='text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider'>
                  <th className='pb-3 pr-4'>
                    <button
                      onClick={() => toggleSort('name')}
                      className='flex items-center gap-2 hover:text-gray-900 dark:hover:text-gray-200'
                    >
                      Organization <SortIcon field='name' />
                    </button>
                  </th>
                  <th className='pb-3 px-4 text-center'>
                    <button
                      onClick={() => toggleSort('locations')}
                      className='flex items-center gap-2 hover:text-gray-900 dark:hover:text-gray-200'
                    >
                      Locations <SortIcon field='locations' />
                    </button>
                  </th>
                  <th className='pb-3 px-4'>
                    <button
                      onClick={() => toggleSort('status')}
                      className='flex items-center gap-2 hover:text-gray-900 dark:hover:text-gray-200'
                    >
                      Status <SortIcon field='status' />
                    </button>
                  </th>
                  <th className='pb-3 px-4'>
                    <button
                      onClick={() => toggleSort('progress')}
                      className='flex items-center gap-2 hover:text-gray-900 dark:hover:text-gray-200'
                    >
                      Progress <SortIcon field='progress' />
                    </button>
                  </th>
                  <th className='pb-3 px-4'>IT</th>
                  <th className='pb-3 px-4'>Data</th>
                  <th className='pb-3 px-4'>
                    <button
                      onClick={() => toggleSort('updated')}
                      className='flex items-center gap-2 hover:text-gray-900 dark:hover:text-gray-200'
                    >
                      Updated <SortIcon field='updated' />
                    </button>
                  </th>
                  <th className='pb-3 px-4 w-24'></th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200 dark:divide-gray-700'>
                {filteredOrganizations.map((org) => {
                  const isExpanded = expandedOrgId === org.id;
                  const stage = getCurrentStage(org.status);
                  const progressColor = getProgressBarColor(org.progressPercentage);

                  return (
                    <React.Fragment key={org.id}>
                    <tr
                      className='hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors'
                    >
                      {/* Organization Name */}
                      <td className='py-3 pr-4'>
                        <div className='flex items-center gap-2'>
                          {org.logo_url ? (
                            <div className='w-8 h-8 rounded overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800'>
                              <img
                                src={org.logo_url}
                                alt={`${org.name} logo`}
                                className='w-full h-full object-cover'
                              />
                            </div>
                          ) : (
                            <Building2 className='h-5 w-5 text-gray-400' />
                          )}
                          <span className='font-medium text-gray-900 dark:text-gray-100'>
                            {org.name}
                          </span>
                        </div>
                      </td>

                      {/* Locations Count */}
                      <td className='py-3 px-4 text-center'>
                        <span className='font-semibold text-gray-900 dark:text-gray-100'>
                          {org.locationCount}
                        </span>
                      </td>

                      {/* Status Badge */}
                      <td className='py-3 px-4'>
                        <div className='flex flex-wrap items-center gap-1.5'>
                          <Badge className={stage.color}>{stage.displayName}</Badge>
                          {locallyArchivedIds.has(org.id) && (
                            <Badge className='bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-xs'>
                              ARCHIVE PENDING
                            </Badge>
                          )}
                        </div>
                      </td>

                      {/* Progress Bar */}
                      <td className='py-3 px-4'>
                        <div className='flex items-center gap-2'>
                          <div className='w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2'>
                            <div
                              className={`h-2 rounded-full bg-linear-to-r ${progressColor}`}
                              style={{ width: `${org.progressPercentage}%` }}
                            />
                          </div>
                          <span className='text-xs font-semibold text-gray-700 dark:text-gray-300 min-w-max'>
                            {org.progressPercentage}%
                          </span>
                        </div>
                      </td>

                      {/* IT Status */}
                      <td className='py-3 px-4'>
                        <div className='text-xs text-center'>
                          <div className='font-semibold text-gray-900 dark:text-gray-100'>
                            {org.itTasksCompleted}/{org.itTasksCompleted + org.itTasksPending}
                          </div>
                          {org.itTasksPending > 0 && (
                            <div className='text-yellow-600 dark:text-yellow-400 text-xs'>
                              {org.itTasksPending} pending
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Data Status */}
                      <td className='py-3 px-4'>
                        <div className='text-xs text-center'>
                          <div className='font-semibold text-gray-900 dark:text-gray-100'>
                            {org.dataTasksCompleted}/{org.dataTasksCompleted + org.dataTasksPending}
                          </div>
                          {org.dataTasksPending > 0 && (
                            <div className='text-yellow-600 dark:text-yellow-400 text-xs'>
                              {org.dataTasksPending} pending
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Last Updated */}
                      <td className='py-3 px-4 text-xs text-gray-600 dark:text-gray-400'>
                        {formatDate(org.lastActivityAt)}
                      </td>

                      {/* Actions (expand + archive) */}
                      <td className='py-3 px-4'>
                        <div className='flex items-center justify-end gap-1'>
                          {/* Archive button — SYSTEM_ADMIN only, on ACTIVATED orgs not yet archived */}
                          {user?.role === 'SYSTEM_ADMIN' &&
                            org.status === 'ACTIVATED' &&
                            !locallyArchivedIds.has(org.id) && (
                              <button
                                className='inline-flex items-center gap-1 text-xs text-amber-600 hover:underline dark:text-amber-400'
                                title='Archive organization'
                                onClick={() =>
                                  setArchiveTarget({
                                    id: org.id,
                                    name: org.name,
                                    locationCount: org.locationCount,
                                  })
                                }
                              >
                                <Archive className='h-3.5 w-3.5' />
                                Archive
                              </button>
                            )}

                          <Button
                            onClick={() =>
                              setExpandedOrgId(isExpanded ? null : org.id)
                            }
                            size='sm'
                            variant='ghost'
                          >
                            {isExpanded ? (
                              <ChevronUp className='h-4 w-4' />
                            ) : (
                              <ChevronDown className='h-4 w-4' />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {/* Expanded Row Details - Inline */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className='py-0'>
                          <ExpandedOrgDetails orgId={org.id} onLocationChanged={onLocationChanged} />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {/* Remove old expanded details section */}
            {/* {false && expandedOrgId && (
              <div className='mt-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700'>
                {(() => {
                  const org = filteredOrganizations.find((o) => o.id === expandedOrgId);
                  if (!org) return null;

                  return (
                    <div className='space-y-2 text-sm'>
                      <div className='flex justify-between'>
                        <span className='text-gray-600 dark:text-gray-400'>Organization ID:</span>
                        <span className='font-mono text-gray-900 dark:text-gray-100'>{org.id}</span>
                      </div>
                      <div className='flex justify-between'>
                        <span className='text-gray-600 dark:text-gray-400'>Current Stage:</span>
                        <span className='text-gray-900 dark:text-gray-100'>
                          {getCurrentStage(org.status).displayName}
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span className='text-gray-600 dark:text-gray-400'>Created:</span>
                        <span className='text-gray-900 dark:text-gray-100'>
                          {formatDate(org.created_at)}
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span className='text-gray-600 dark:text-gray-400'>Last Activity:</span>
                        <span className='text-gray-900 dark:text-gray-100'>
                          {formatDate(org.lastActivityAt)}
                        </span>
                      </div>
                      <div className='pt-2 border-t border-gray-200 dark:border-gray-700'>
                        <div className='text-gray-600 dark:text-gray-400 font-semibold mb-2'>
                          Task Breakdown:
                        </div>
                        <div className='grid grid-cols-3 gap-2 text-xs'>
                          <div>
                            <div className='text-gray-600 dark:text-gray-400'>IT Tasks</div>
                            <div className='text-gray-900 dark:text-gray-100 font-semibold'>
                              {org.itTasksCompleted} / {org.itTasksCompleted + org.itTasksPending}
                            </div>
                          </div>
                          <div>
                            <div className='text-gray-600 dark:text-gray-400'>Data Tasks</div>
                            <div className='text-gray-900 dark:text-gray-100 font-semibold'>
                              {org.dataTasksCompleted} / {org.dataTasksCompleted + org.dataTasksPending}
                            </div>
                          </div>
                          <div>
                            <div className='text-gray-600 dark:text-gray-400'>Platform Tasks</div>
                            <div className='text-gray-900 dark:text-gray-100 font-semibold'>
                              {org.platformTasksCompleted} / {org.platformTasksCompleted + org.platformTasksPending}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )} */}
          </div>
        )}

        {/* Pagination */}
        <div className='mt-6 flex items-center justify-between'>
          <div className='text-sm text-gray-600 dark:text-gray-400'>
            Showing {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, allFilteredOrganizations.length)} of{' '}
            {allFilteredOrganizations.length} organizations
          </div>

          {totalPages > 1 && (
            <div className='flex items-center gap-2'>
              <Button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                variant='outline'
                size='sm'
              >
                <ChevronLeft className='h-4 w-4' />
              </Button>

              <div className='flex items-center gap-1'>
                {Array.from({ length: totalPages }).map((_, i) => {
                  const page = i + 1;
                  return (
                    <Button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      variant={currentPage === page ? 'default' : 'outline'}
                      size='sm'
                      className='min-w-10'
                    >
                      {page}
                    </Button>
                  );
                })}
              </div>

              <Button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                variant='outline'
                size='sm'
              >
                <ChevronRight className='h-4 w-4' />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>

    {/* Archive confirmation dialog */}
    {archiveTarget && (
      <ArchiveOrgDialog
        open={!!archiveTarget}
        onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}
        organizationId={archiveTarget.id}
        organizationName={archiveTarget.name}
        activeLocationCount={archiveTarget.locationCount}
        onSuccess={() => {
          setLocallyArchivedIds((prev) => new Set([...prev, archiveTarget.id]));
          setArchiveTarget(null);
          onOrgArchived?.();
        }}
      />
    )}
    </>
  );
}
